// The Negotiation tab's two actions (Lane 1354, step S3c; Ryan's review, PLAN 13b items
// 8 and 9): "Add all targets to round N feedback" (the follow-up after the meeting:
// nothing is sent until the buyer releases the draft) and "Build my negotiation deck"
// (one click: the server picks the focus parts and queues the build).
//
// The deck's state is the server's (`deck`, from the tab read; Lane 1362): a build in
// progress shows whenever the buyer comes back, with Meeting Prep's progress panel, and
// opening Meeting Prep attaches to that same build (it only reads its status). Before,
// the state lived in this component's memory: leaving the tab lost it, a failed build
// was never shown, and the next click quietly started another build.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { endSentence } from './sourcingApi';
import NegotiationDeckProgress from './NegotiationDeckProgress';

function plural(n, one, many) { return `${n.toLocaleString()} ${n === 1 ? one : many}`; }

function when(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-US', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

// `deck`: the server's newest negotiation deck for this supplier (or null), as
// {deck_version_id, state: building|ready|failed, current, requested_at}; `current` is
// true when "Build my negotiation deck" would give this same deck now.
// `onDeckChanged`: read the tab again; resolves once the new state is on screen.
export default function NegotiationActions({ api, eventId, supplier, feedback, deck, onChanged, onDeckChanged, onOpenRound }) {
  const navigate = useNavigate();
  const [copy, setCopy] = useState(null);    // null | 'confirm' | 'busy' | {done} | {error}
  const [starting, setStarting] = useState(false);
  const [deckError, setDeckError] = useState('');
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const name = supplier.supplier_name;
  const round = feedback.round;
  const n = feedback.targets;

  const addTargets = async () => {
    setCopy('busy');
    try {
      const out = await api.negotiationAction(eventId, 'feedback', {
        supplier_id: supplier.supplier_id, round: feedback.round, targets_digest: feedback.targets_digest,
        disclosure_id: feedback.draft ? feedback.draft.disclosure_id : null,
        content_digest: feedback.draft ? feedback.draft.content_digest : null,
      });
      if (alive.current) setCopy({ done: out.result });
    } catch (e) {
      if (alive.current) setCopy({ error: e.message });
    } finally {
      if (alive.current) onChanged();
    }
  };
  const build = async () => {
    setStarting(true);
    setDeckError('');
    try {
      await api.negotiationAction(eventId, 'deck', { supplier_id: supplier.supplier_id });
    } catch (e) {
      if (alive.current) setDeckError(e.message);
    }
    // The server's state, either way: the new build, or the same one again.
    try { await onDeckChanged(); } catch (e) { /* the tab shows its own refresh error */ }
    if (alive.current) setStarting(false);
  };
  const openDeck = () => navigate(`/decks/meeting-prep?templateDeck=${deck.deck_version_id}`);
  const state = deck ? deck.state : null;
  const building = state === 'building';
  const readyNow = state === 'ready' && deck.current;
  const failedNow = state === 'failed' && deck.current;
  const olderReady = state === 'ready' && !deck.current;
  // One deck control at a time: a build of these offers is running, ready or failed
  // (its own panel below says what to do), or a new deck can be built.
  const canBuild = !(building && deck.current) && !readyNow && !failedNow;

  return (
    <>
      <span className="srce-row srce-push neg-actions">
        <button type="button" className="srce-btn" disabled={!n || copy === 'busy'}
          title={n ? undefined : 'No part has a target yet'}
          onClick={() => setCopy('confirm')}>Add all targets to round {round} feedback</button>
        {canBuild && (
          <button type="button" className="srce-btn srce-btn--primary" disabled={starting}
            onClick={build}>{starting ? 'Starting…' : 'Build my negotiation deck'}</button>)}
      </span>
      {(copy === 'confirm' || copy === 'busy') && (
        <div className="srce-notice neg-inline" role="dialog" aria-label="Add targets to feedback">
          <div>
            <div className="srce-strong">{n === 1 ? 'Add the target' : `Add all ${plural(n, 'target', 'targets')}`} to {name}'s round {round} feedback?</div>
            <div>This is the follow-up after the meeting. When you release the feedback, {name} sees the
              target for every part in the supplier portal, including the parts where we ask them to hold
              today's price, and updates their offers there. Nothing is sent until you release it
              in Rounds and feedback.</div>
          </div>
          <span className="srce-row">
            <button type="button" className="srce-btn srce-btn--primary" disabled={copy === 'busy'} onClick={addTargets}>Add targets</button>
            <button type="button" className="ll-clr" disabled={copy === 'busy'} onClick={() => setCopy(null)}>Cancel</button>
          </span>
        </div>
      )}
      {copy && copy.done && (
        <div className="srce-notice neg-inline" role="status">
          <span>
            {plural(copy.done.targets, 'target', 'targets')} added to {name}'s round {copy.done.round} feedback
            draft{copy.done.created ? ' (a new draft)' : ''}. Nothing is sent until you release it in Rounds and feedback.
          </span>
          {onOpenRound && <button type="button" className="ll-clr" onClick={onOpenRound}>Open Rounds and feedback</button>}
        </div>
      )}
      {copy && copy.error && (
        <div className="srce-error neg-inline" role="alert">
          {copy.error} <button type="button" className="ll-clr" onClick={() => setCopy(null)}>Close</button>
        </div>
      )}
      {building && (
        <div className="neg-inline neg-deck" data-testid="neg-deck-building">
          <NegotiationDeckProgress key={deck.deck_version_id} api={api} deckVersionId={deck.deck_version_id}
            onSettled={() => { onDeckChanged().catch(() => {}); }} />
          <div className="srce-row neg-deck-f">
            <span className="srce-mut">
              {endSentence(`Building the negotiation deck for ${name}`)} You can keep working or leave this tab; the build continues.
              {deck.current ? '' : ' It uses earlier offers or targets.'}
            </span>
            <button type="button" className="srce-btn srce-push" onClick={openDeck}>Open in Meeting Prep</button>
          </div>
        </div>
      )}
      {readyNow && (
        <div className="srce-notice neg-inline" role="status" data-testid="neg-deck-ready">
          <span>{endSentence(`Your negotiation deck for ${name} is ready`)}</span>
          <button type="button" className="srce-btn srce-btn--primary" onClick={openDeck}>Open the deck</button>
        </div>
      )}
      {olderReady && (
        <div className="srce-notice neg-inline" role="status" data-testid="neg-deck-older">
          <span>{endSentence(`Your last negotiation deck for ${name}${deck.requested_at ? ` (${when(deck.requested_at)})` : ''} uses earlier offers or targets`)} Build a new one for the offers and targets now.</span>
          <button type="button" className="srce-btn" onClick={openDeck}>Open that deck</button>
        </div>
      )}
      {failedNow && (
        <div className="srce-error neg-inline" role="alert" data-testid="neg-deck-failed">
          <span>{endSentence(`The negotiation deck for ${name} could not be built`)} No deck was published. Try again.</span>
          <button type="button" className="srce-btn srce-btn--primary" disabled={starting} onClick={build}>{starting ? 'Starting…' : 'Try again'}</button>
        </div>
      )}
      {deckError && (
        <div className="srce-error neg-inline" role="alert">
          {deckError} <button type="button" className="ll-clr" onClick={() => setDeckError('')}>Close</button>
        </div>
      )}
    </>
  );
}
